import { useEffect, useState } from "react";
import { Button, Screen, Spinner, Text } from "@ferrivia/ui";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import type { OpenSummaryOptions } from "../app/useAppContext";
import { useDismissOnBack } from "../hooks/useDismissOnBack";
import { useScreenBackground } from "../hooks/useScreenBackground";
import { SummaryReader } from "./SummaryReader";
import { useQuestionnaireSummary } from "./useQuestionnaireSummary";
import s from "./SummaryOverlay.module.css";

export interface SummaryOverlayProps {
  summary: ({ id: string } & OpenSummaryOptions) | null;
  onClose: () => void;
}

/** Same shape as QuestionnaireOverlay: not a route, so a client's health data
    is never linkable. `useDismissOnBack` runs on every render because it is
    what notices open and close; the content below mounts only while open. */
export function SummaryOverlay({ summary, onClose }: SummaryOverlayProps) {
  useDismissOnBack(summary !== null, onClose, "summary");
  if (!summary) return null;
  return <SummaryOverlayContent key={summary.id} summary={summary} onClose={onClose} />;
}

function SummaryOverlayContent({
  summary,
  onClose,
}: {
  summary: { id: string } & OpenSummaryOptions;
  onClose: () => void;
}) {
  const { t } = useTranslation("client");
  const tracker = useTracker();
  useScreenBackground("summary", "--color-surface-default");
  const { state, refetch } = useQuestionnaireSummary(summary.id);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    tracker.track("summary.opened", { source: summary.source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Screen ref={setContainer} className={s.root}>
      {state.status === "success" ? (
        <SummaryReader
          summary={state.summary}
          path={summary.path ?? state.summary.cta}
          container={container}
          onClose={onClose}
        />
      ) : (
        <div className={s.centered}>
          {state.status === "loading" ? (
            <Spinner />
          ) : (
            <>
              <Text as="p" size="md" className={s.errorText} role="alert">
                {t("summary.loadFailed", "Raporttia ei voitu ladata.")}
              </Text>
              <Button onClick={refetch}>{t("summary.retry", "Yritä uudelleen")}</Button>
              <Button variant="ghost" onClick={onClose}>
                {t("summary.close", "Sulje")}
              </Button>
            </>
          )}
        </div>
      )}
    </Screen>
  );
}
